import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Pause, PlayArrow, VolumeOff, VolumeUp } from '@material-ui/icons';

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoPlayer = forwardRef(({ src, autoPlay = true }, ref) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);

    const togglePlay = useCallback(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        if (video.paused) {
            video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        } else {
            video.pause();
            setIsPlaying(false);
        }
    }, []);

    const toggleMute = useCallback(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        video.muted = !video.muted;
        setIsMuted(video.muted);
    }, []);

    const stop = useCallback(() => {
        const video = videoRef.current;
        if (video) {
            video.pause();
            video.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentTime(0);
    }, []);

    useImperativeHandle(ref, () => ({
        stop,
        togglePlay,
    }), [stop, togglePlay]);

    const handleLoadedMetadata = () => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        setDuration(video.duration || 0);

        if (autoPlay) {
            video.muted = false;
            setIsMuted(false);
            video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
    };

    const handleTimeUpdate = () => {
        if (isSeeking) {
            return;
        }

        const video = videoRef.current;
        if (video) {
            setCurrentTime(video.currentTime);
        }
    };

    const handleSeekStart = () => {
        setIsSeeking(true);
    };

    const handleSeek = (e) => {
        const value = Number(e.target.value);
        const video = videoRef.current;

        setCurrentTime(value);
        if (video) {
            video.currentTime = value;
        }
    };

    const handleSeekEnd = () => {
        setIsSeeking(false);
    };

    return (
        <div className="video-player">
            <video
                ref={videoRef}
                className="video-player-media"
                src={src}
                playsInline
                onClick={togglePlay}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
            />
            <div className="video-player-controls">
                <button
                    type="button"
                    className="video-player-control"
                    onClick={togglePlay}
                    aria-label={isPlaying ? 'Pause' : 'Abspielen'}
                >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                </button>
                <span className="video-player-time" aria-hidden="true">
                    {formatTime(currentTime)}
                </span>
                <input
                    type="range"
                    className="video-player-progress"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={currentTime}
                    onMouseDown={handleSeekStart}
                    onTouchStart={handleSeekStart}
                    onChange={handleSeek}
                    onInput={handleSeek}
                    onMouseUp={handleSeekEnd}
                    onTouchEnd={handleSeekEnd}
                    aria-label="Wiedergabefortschritt"
                    aria-valuemin={0}
                    aria-valuemax={duration || 0}
                    aria-valuenow={currentTime}
                />
                <span className="video-player-time" aria-hidden="true">
                    {formatTime(duration)}
                </span>
                <button
                    type="button"
                    className="video-player-control"
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Ton einschalten' : 'Stummschalten'}
                >
                    {isMuted ? <VolumeOff /> : <VolumeUp />}
                </button>
            </div>
        </div>
    );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
